import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
    AIChatApiError, getHistory, getUsage, isAIChatConfigured, isUsageFooter, streamChat
} from '../utils/aiChatApi';

// Lives above the routes (not in Layout) so the drawer's open state and the
// conversation survive page navigation — every page mounts its own Layout.
// The value is null when no Insight AI backend is configured, which hides every entry point.

export const AIChatContext = createContext();

export const useAIChat = () => useContext(AIChatContext);

// The backend refuses a message without a question, so files sent on their own carry
// this one. It isn't shown: the user's message is just the attachments.
export const FILES_ONLY_QUESTION = 'File attached';

const ANSWER_ERROR = 'Insight AI couldn\'t finish this answer. Please try again.';
const CUT_OFF_ERROR = 'The answer was cut off before it finished. Please try again.';

// The backend reports unexpected failures as `String(err)` ("Error: 400 {...}"), which
// isn't for users. Its own messages, like the time limit one, are plain sentences.
export const streamErrorMessage = message => (
    typeof message === 'string' && message && !/^[A-Za-z]*Error\b/.test(message) ? message : ANSWER_ERROR
);

// Charts only come with a file name ("weekly_ch4_trend.png"), so the title is made from that.
export const chartTitle = filename => {
    const name = (filename || '').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : 'Chart';
};

const toImage = (src, filename) => ({
    id: uuidv4(),
    src,
    title: chartTitle(filename),
    fileName: filename || 'zelp-ai-chart.png',
});

export const fromHistoryEntry = entry => ({
    id: `history-${entry.id}`,
    role: entry.role,
    // the stored question of a files-only message is the hidden placeholder
    text: entry.role === 'user' && entry.attachments?.length && entry.text === FILES_ONLY_QUESTION
        ? '' : entry.text || '',
    attachments: (entry.attachments || []).map(file => ({ id: uuidv4(), name: file.filename })),
    images: (entry.charts || []).map(chart => toImage(chart.url, chart.filename)),
    status: 'done',
});

export const toUsage = ({ spentUsd, budgetUsd, resetsAt }) => ({
    label: 'Weekly usage',
    // A farm without devices has no budget, and the backend refuses every question for it.
    percentage: budgetUsd > 0 ? Math.min(100, Math.round((spentUsd / budgetUsd) * 100)) : 100,
    resetsAt,
});

const AIChatProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isResponding, setIsResponding] = useState(false);
    // idle -> loading -> loaded | error; loaded once per signed-in user
    const [historyStatus, setHistoryStatus] = useState('idle');
    const [usage, setUsage] = useState(null);
    // Who the chat is for and which farm its usage counts against. Synced by the
    // drawer, which renders inside the User and ActiveFarm providers; this doesn't.
    const [session, setSession] = useState({ userId: null, farmId: null });

    const abortRef = useRef(null);
    // Bumped whenever the conversation is thrown away (new chat, another user), so
    // responses still in flight for the old one are dropped instead of applied.
    const generationRef = useRef(0);
    const usageRequestRef = useRef(0);
    const previousUserRef = useRef(null);

    const openChat = useCallback(() => setIsOpen(true), []);
    const closeChat = useCallback(() => setIsOpen(false), []);

    const syncSession = useCallback(({ userId, farmId }) => {
        setSession(prev => (prev.userId === userId && prev.farmId === farmId ? prev : { userId, farmId }));
    }, []);

    // Signing out, or in as someone else, must not leave the previous user's conversation behind.
    useEffect(() => {
        if (previousUserRef.current === session.userId) return;
        previousUserRef.current = session.userId;
        generationRef.current += 1;
        abortRef.current?.abort();
        setMessages([]);
        setHistoryStatus('idle');
        setIsOpen(false);
    }, [session.userId]);

    const refreshUsage = useCallback(async () => {
        usageRequestRef.current += 1;
        const requestId = usageRequestRef.current;
        if (!session.userId) return;
        try {
            const result = await getUsage({ farmId: session.farmId });
            if (requestId === usageRequestRef.current) setUsage(toUsage(result));
        }
        catch (e) {
            // informational only: the bar keeps its last value
        }
    }, [session.userId, session.farmId]);

    // usage is per farm, so a farm switch invalidates it
    useEffect(() => setUsage(null), [session.userId, session.farmId]);

    useEffect(() => {
        if (isOpen) refreshUsage();
    }, [isOpen, refreshUsage]);

    const loadHistory = useCallback(async () => {
        const generation = generationRef.current;
        setHistoryStatus('loading');
        try {
            const history = await getHistory();
            if (generation !== generationRef.current) return;
            // prepended: a question sent while this was loading belongs after it
            setMessages(prev => [...history.map(fromHistoryEntry), ...prev]);
            setHistoryStatus('loaded');
        }
        catch (e) {
            if (generation === generationRef.current) setHistoryStatus('error');
        }
    }, []);

    useEffect(() => {
        if (isOpen && session.userId && historyStatus === 'idle') loadHistory();
    }, [isOpen, session.userId, historyStatus, loadHistory]);

    const stopResponse = useCallback(() => abortRef.current?.abort(), []);

    const sendMessage = useCallback(async ({ text, files = [] }) => {
        const typed = text.trim();
        if ((!typed && !files.length) || abortRef.current) return;
        const question = typed || FILES_ONLY_QUESTION;

        const generation = generationRef.current;
        const answerId = uuidv4();
        const controller = new AbortController();
        abortRef.current = controller;
        setIsResponding(true);
        setMessages(prev => [
            ...prev,
            {
                id: uuidv4(),
                role: 'user',
                text: typed,
                attachments: files.map(file => ({ id: uuidv4(), name: file.name })),
                status: 'done',
            },
            {
                id: answerId, role: 'assistant', text: '', images: [], status: 'streaming'
            },
        ]);

        const updateAnswer = change => {
            if (generation !== generationRef.current) return;
            setMessages(prev => prev.map(message => (
                message.id === answerId ? { ...message, ...change(message) } : message
            )));
        };

        let ended = false;
        try {
            await streamChat({
                question,
                files,
                farmId: session.farmId,
                signal: controller.signal,
                onEvent: ({ event, data }) => {
                    if (event === 'text') {
                        if (typeof data === 'string' && !isUsageFooter(data)) {
                            updateAnswer(message => ({ text: message.text + data }));
                        }
                    }
                    else if (event === 'chart') {
                        if (data?.data) {
                            const src = `data:${data.mimeType || 'image/png'};base64,${data.data}`;
                            updateAnswer(message => ({ images: [...message.images, toImage(src, data.filename)] }));
                        }
                    }
                    else if (event === 'done') {
                        ended = true;
                        updateAnswer(() => ({ status: 'done' }));
                    }
                    else if (event === 'error') {
                        ended = true;
                        updateAnswer(() => ({ status: 'error', error: streamErrorMessage(data?.message) }));
                    }
                },
            });
            // a stream that closes without `done` or `error` was cut off on the way
            if (!ended) updateAnswer(() => ({ status: 'error', error: CUT_OFF_ERROR }));
        }
        catch (err) {
            if (controller.signal.aborted) {
                updateAnswer(() => ({ status: 'stopped' }));
            }
            else {
                updateAnswer(() => ({
                    status: 'error',
                    error: err instanceof AIChatApiError ? err.message : ANSWER_ERROR,
                }));
            }
        }
        finally {
            if (abortRef.current === controller) {
                abortRef.current = null;
                setIsResponding(false);
            }
            if (generation === generationRef.current) refreshUsage();
        }
    }, [session.farmId, refreshUsage]);

    // Only clears the screen: the backend has no separate conversations yet, so it keeps
    // answering with its last 20 messages as context and returns them on the next page load.
    const newChat = useCallback(() => {
        generationRef.current += 1;
        abortRef.current?.abort();
        setMessages([]);
        setHistoryStatus('loaded');
    }, []);

    const value = useMemo(() => ({
        isOpen,
        openChat,
        closeChat,
        messages,
        isResponding,
        sendMessage,
        stopResponse,
        newChat,
        historyStatus,
        retryHistory: loadHistory,
        usage,
        syncSession,
    }), [
        isOpen, openChat, closeChat, messages, isResponding, sendMessage, stopResponse, newChat,
        historyStatus, loadHistory, usage, syncSession
    ]);

    return (
        <AIChatContext.Provider value={isAIChatConfigured() ? value : null}>
            {children}
        </AIChatContext.Provider>
    );
};

export default AIChatProvider;
